import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { qk } from '@/lib/queryClient'
import { NOTIFICATION_EVENTS } from '@/lib/types'
import type {
  NotificationEvent,
  NotificationPreference,
  NotificationPreferencesResponse,
} from '@/lib/types'
import { useToast } from './useToast'

const NOTIFICATIONS_POLL_MS = 60_000

export function useNotifications(options: { unreadOnly?: boolean; limit?: number } = {}) {
  const unreadOnly = options.unreadOnly ?? false
  return useQuery({
    queryKey: qk.notifications(unreadOnly),
    queryFn: ({ signal }) =>
      api.notifications.list({ unreadOnly, limit: options.limit }, signal),
    refetchInterval: NOTIFICATIONS_POLL_MS,
    refetchIntervalInBackground: false,
  })
}

export function useMarkNotificationsRead() {
  const queryClient = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: (ids?: string[]) => api.notifications.markRead(ids),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
    onError: (error) => toast.fromError(error, 'Could not mark as read'),
  })
}

export type PreferenceMap = Record<NotificationEvent, boolean>

/** A missing row means enabled — the server treats preferences as opt-out. */
function toPreferenceMap(data: NotificationPreferencesResponse): PreferenceMap {
  const list: NotificationPreference[] = Array.isArray(data) ? data : (data.preferences ?? [])
  const map = Object.fromEntries(
    NOTIFICATION_EVENTS.map((event) => [event, true]),
  ) as PreferenceMap
  for (const row of list) {
    if (NOTIFICATION_EVENTS.includes(row.event)) map[row.event] = row.enabled
  }
  return map
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: qk.notificationPreferences,
    queryFn: ({ signal }) => api.notifications.preferences(signal),
    select: toPreferenceMap,
    staleTime: 5 * 60_000,
  })
}

export function useSaveNotificationPreferences() {
  const queryClient = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: (map: PreferenceMap) =>
      api.notifications.savePreferences(
        NOTIFICATION_EVENTS.map((event) => ({ event, enabled: map[event] })),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.notificationPreferences })
      toast.success('Notification settings saved')
    },
    onError: (error) => toast.fromError(error, 'Could not save notification settings'),
  })
}
